import { CSSProperties, useEffect, useState } from "react";
import { IReservationSettingsUpdate } from "../models/IReservation";
import { getReservationSettings, putReservationSettings } from "../services/reservationService";

interface Props {
  restaurantId: string;
}

const DEFAULTS: Omit<IReservationSettingsUpdate, "restourantId"> = {
  isEnabled: false,
  defaultDurationMinutes: 120,
  bufferMinutes: 15,
  minLeadTimeMinutes: 0,
  maxAdvanceDays: 30,
  maxPartySize: 10,
};

function minutesToHuman(total: number): string {
  if (!total || total <= 0) return "0 dk";
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days} gün`);
  if (hours) parts.push(`${hours} sa`);
  if (mins) parts.push(`${mins} dk`);
  return parts.join(" ");
}

const fieldInputStyle: CSSProperties = { borderRadius: 10, border: "1.5px solid #e5e7eb" };

interface NumberFieldProps {
  label: string;
  description: string;
  suffix: string;
  value: number;
  onChange: (v: number) => void;
  showHuman?: boolean;
}

function NumberField({ label, description, suffix, value, onChange, showHuman }: NumberFieldProps) {
  return (
    <div style={{
      borderRadius: 14, border: "1.5px solid #e5e7eb", background: "#fff",
      padding: "14px 16px", boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    }}>
      <label style={{ fontSize: "0.84rem", fontWeight: 700, color: "#111827", display: "block", marginBottom: 4 }}>
        {label}
      </label>
      <p style={{ fontSize: "0.76rem", color: "#6b7280", margin: "0 0 10px" }}>{description}</p>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="number"
          min={0}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="form-control"
          style={fieldInputStyle}
        />
        <span style={{ fontSize: "0.78rem", color: "#9ca3af", fontWeight: 600, whiteSpace: "nowrap" }}>{suffix}</span>
      </div>
      {showHuman && value > 0 && (
        <div style={{ fontSize: "0.72rem", color: "#0d9488", marginTop: 6, fontWeight: 600 }}>
          <i className="bi bi-info-circle me-1"></i>≈ {minutesToHuman(value)}
        </div>
      )}
    </div>
  );
}

export default function ReservationSettingsPanel({ restaurantId }: Props) {
  const [settings, setSettings] = useState<IReservationSettingsUpdate>({ restourantId: restaurantId, ...DEFAULTS });
  const [isConfigured, setIsConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    setError("");
    setSaved(false);
    getReservationSettings(restaurantId)
      .then((res) => {
        const data = res.data;
        if (data) {
          setSettings({
            restourantId: data.restourantId,
            isEnabled: data.isEnabled,
            defaultDurationMinutes: data.defaultDurationMinutes,
            bufferMinutes: data.bufferMinutes,
            minLeadTimeMinutes: data.minLeadTimeMinutes,
            maxAdvanceDays: data.maxAdvanceDays,
            maxPartySize: data.maxPartySize,
          });
          setIsConfigured(data.isConfigured);
        }
      })
      .catch(() => {
        setSettings({ restourantId: restaurantId, ...DEFAULTS });
        setIsConfigured(false);
      })
      .finally(() => setLoading(false));
  }, [restaurantId]);

  const setField = <K extends keyof IReservationSettingsUpdate>(key: K, value: IReservationSettingsUpdate[K]) => {
    setSettings((p) => ({ ...p, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    setSaving(true);
    setError("");
    setSaved(false);
    putReservationSettings({ ...settings, restourantId: restaurantId })
      .then(() => {
        setIsConfigured(true);
        setSaved(true);
      })
      .catch(() => setError("Ayarlar kaydedilemedi."))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px 0" }}>
        <div className="spinner-border" style={{ color: "#0d9488" }} role="status" />
      </div>
    );
  }

  return (
    <div>
      {/* Ana aç/kapa kartı */}
      <div
        onClick={() => setField("isEnabled", !settings.isEnabled)}
        style={{
          borderRadius: 16, padding: "18px 20px", marginBottom: 16, cursor: "pointer", userSelect: "none",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
          background: settings.isEnabled ? "linear-gradient(135deg, #f0fdfa, #ccfbf1)" : "#f9fafb",
          border: `1.5px solid ${settings.isEnabled ? "#99f6e4" : "#e5e7eb"}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0,
            background: settings.isEnabled ? "#0d9488" : "#d1d5db",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <i className="bi bi-calendar-check-fill" style={{ color: "#fff", fontSize: "1.1rem" }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: "0.95rem", color: "#111827" }}>Online Rezervasyon</div>
            <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
              Müşteriler bu restoran için online rezervasyon yapabilsin mi?
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isConfigured && (
            <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#eff6ff", color: "#2563eb" }}>
              <i className="bi bi-check-circle-fill me-1"></i>Yapılandırıldı
            </span>
          )}
          <span style={{
            fontSize: "0.8rem", fontWeight: 700,
            color: settings.isEnabled ? "#0f766e" : "#6b7280",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <i className={`bi ${settings.isEnabled ? "bi-toggle2-on" : "bi-toggle2-off"}`} style={{ fontSize: "1.5rem" }}></i>
            {settings.isEnabled ? "Açık" : "Kapalı"}
          </span>
        </div>
      </div>

      {/* Ayar alanları */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginBottom: 16 }}>
        <NumberField
          label="Varsayılan Rezervasyon Süresi"
          description="Bir rezervasyon için masa varsayılan olarak bu kadar süre ayrılır."
          suffix="dakika"
          value={settings.defaultDurationMinutes}
          onChange={(v) => setField("defaultDurationMinutes", v)}
          showHuman
        />
        <NumberField
          label="Masa Tampon Süresi"
          description="Rezervasyon bitiminden sonra masa bu süre boyunca tekrar rezerve edilemez."
          suffix="dakika"
          value={settings.bufferMinutes}
          onChange={(v) => setField("bufferMinutes", v)}
          showHuman
        />
        <NumberField
          label="Minimum Önceden Rezervasyon Süresi"
          description="Müşteriler, rezervasyon saatinden en az bu kadar süre önce talep gönderebilir."
          suffix="dakika"
          value={settings.minLeadTimeMinutes}
          onChange={(v) => setField("minLeadTimeMinutes", v)}
          showHuman
        />
        <NumberField
          label="Maksimum İleri Tarih"
          description="Müşteriler en fazla bu kadar gün sonrasına rezervasyon yapabilir."
          suffix="gün"
          value={settings.maxAdvanceDays}
          onChange={(v) => setField("maxAdvanceDays", v)}
        />
        <NumberField
          label="Maksimum Kişi Sayısı"
          description="Tek bir rezervasyonda izin verilen en fazla kişi sayısı."
          suffix="kişi"
          value={settings.maxPartySize}
          onChange={(v) => setField("maxPartySize", v)}
        />
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10, padding: "10px 14px", color: "#b91c1c", fontSize: "0.84rem", marginBottom: 16 }}>
          <i className="bi bi-exclamation-circle me-1"></i>{error}
        </div>
      )}

      {saved && !error && (
        <div style={{ background: "#f0fdf4", border: "1.5px solid #bbf7d0", borderRadius: 10, padding: "10px 14px", color: "#166534", fontSize: "0.84rem", marginBottom: 16 }}>
          <i className="bi bi-check-circle-fill me-1"></i>Ayarlar kaydedildi.
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: "10px 24px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg, #0d9488, #0f766e)",
            color: "#fff", fontWeight: 700, fontSize: "0.86rem",
            cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 8,
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving
            ? <><span className="spinner-border spinner-border-sm"></span> Kaydediliyor...</>
            : <><i className="bi bi-check-lg"></i> Ayarları Kaydet</>
          }
        </button>
      </div>
    </div>
  );
}
